// Centralized user-facing copy for the entire application.
// Update text here to change it everywhere across the site.

export const copy = {
  brand: {
    name: 'Imperian',
  },

  header: {
    casesLink: 'Cases',
    caseDetailLink: 'Case Detail',
  },

  thread: {
    emptyTitle: 'How can I help you today?',
    emptyBody:
      "I'm your case assistant. Ask me to review discovery, draft motions, or research case law.",
    citedSourcesLabel: 'Cited Sources',
  },

  composer: {
    placeholder: 'How can I help you today?',
    disclaimer:
      'Imperian can make mistakes. Verify all information before use.',
    model: 'Sonnet 4.6',
    mode: 'Medium',
  },

  caseList: {
    headerTitle: 'Imperian',
    title: 'Cases',
    subtitle:
      'Review discovery materials, track deadlines, and prepare arguments for your current docket.',
    newCaseButton: 'New Case',
    searchPlaceholder: 'Search by client name or case number...',
    emptyTitle: 'No cases found',
    emptyBody: 'Try adjusting your search or create a new case.',
    cases: [
      {
        id: '1',
        name: 'State v. Thompson',
        description:
          'Felony possession. Reviewing search circumstances and preparing motion to suppress.',
        lastActive: '17 minutes ago',
      },
      {
        id: '2',
        name: 'State v. Rodriguez',
        description:
          'DUI, second offense. Analyzing field sobriety procedures and reviewing recorded evidence.',
        lastActive: '2 hours ago',
      },
      {
        id: '3',
        name: 'State v. Miller',
        description:
          'Burglary, third degree. Developing alibi documentation and reviewing cell site records.',
        lastActive: '1 day ago',
      },
      {
        id: '4',
        name: 'State v. Chen',
        description:
          'Aggravated assault. Self-defense claim. Compiling witness statements and timeline.',
        lastActive: '3 days ago',
      },
      {
        id: '5',
        name: 'State v. Wilson',
        description:
          'Grand larceny. Reviewing surveillance footage and chain-of-custody documentation.',
        lastActive: '1 week ago',
      },
    ],
  },

  caseDetail: {
    backLabel: 'Back to Cases',
    title: 'State v. Thompson',
    subtitle: 'Case No: CR-2024-004421 • Felony Possession',
    composerPlaceholder: 'How can I help you today?',
    model: 'Sonnet 4.6',
    mode: 'Medium',
    memoryTitle: 'Case Summary',
    privilegedLabel: 'Privileged',
    memoryBody:
      'Client has no prior felony record. Defense is focused on the legal basis for the vehicle search. Key witness is the front-seat passenger, Sarah Jenkins, whose account differs materially from the incident report...',
    memoryLastUpdated: 'Last updated 3 days ago',
    instructionsTitle: 'Instructions',
    instructionsBody: "Add instructions to tailor Imperian's responses",
    filesTitle: 'Files',
    capacityLabel: '4% of project capacity used',
    indexingLabel: 'Indexing',
    threads: [
      {
        title: 'Reviewing Motion to Suppress',
        time: '17 minutes ago',
      },
      { title: 'Summary of client interview from 5/20', time: '18 hours ago' },
      { title: "Analysis of prosecution's plea offer", time: '19 hours ago' },
      {
        title: 'Cross-examination preparation for Officer Davis',
        time: '23 hours ago',
      },
      {
        title: 'Fourth Amendment case law research',
        time: '2 days ago',
      },
    ],
    files: [
      { name: 'Police Report - Incident 4421', lines: '12 pages', type: 'PDF' },
      { name: 'Body Cam Footage - Arrest', lines: '15:20', type: 'VIDEO' },
      { name: 'Motion to Suppress - Draft', lines: '8 pages', type: 'WORD' },
    ],
  },

  newCaseModal: {
    title: 'Open New Case File',
    nameLabel: 'Case Name / Client',
    namePlaceholder: 'e.g., State v. Smith',
    summaryLabel: 'Case Summary (Optional)',
    summaryPlaceholder: 'Brief overview of charges and current status...',
    cancel: 'Cancel',
    create: 'Create Case',
  },

  files: {
    title: 'State v. Thompson',
    subtitle: 'Managing 1,242 files',
    filterButton: 'Filter',
    addFilesButton: 'Add files',
    searchPlaceholder: 'Search by filename, content, or metadata...',
    aiCategoriesTitle: 'AI Discovery Categories',
    categories: [
      'Drone Footage',
      'Body Cam',
      'Surveillance',
      'Audio Transcripts',
      'Legal Research',
      'Evidence Photos',
    ],
    selectedSuffix: 'files selected',
    downloadAll: 'Download All',
    delete: 'Delete',
    columns: {
      name: 'Name',
      size: 'Size',
      type: 'Type',
      lastModified: 'Last Modified',
      actions: 'Actions',
    },
    emptySearch: 'No files found matching your search.',
    list: [
      {
        id: '1',
        name: 'Police Report - Incident 4421.pdf',
        size: '124 KB',
        lines: '12 pages',
        type: 'PDF',
        date: '3 days ago',
        ext: 'pdf',
        category: 'Legal',
        preview:
          'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '2',
        name: 'Arrest Warrant - CR-4421.pdf',
        size: '450 KB',
        lines: '2 pages',
        type: 'PDF',
        date: '3 days ago',
        ext: 'pdf',
        category: 'Legal',
        preview:
          'https://images.unsplash.com/photo-1568667256549-094345857637?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '3',
        name: 'Motion to Suppress - Draft.docx',
        size: '12 KB',
        lines: '8 pages',
        type: 'WORD',
        date: '1 week ago',
        ext: 'docx',
        category: 'Legal',
        preview:
          'https://images.unsplash.com/photo-1589330694653-ded6df03f754?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '4',
        name: 'Prosecution Plea Offer.pdf',
        size: '2.4 MB',
        lines: '4 pages',
        type: 'PDF',
        date: '2 days ago',
        ext: 'pdf',
        category: 'Legal',
        preview:
          'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '5',
        name: 'Client Interview Transcript.txt',
        size: '85 KB',
        lines: '1,200 lines',
        type: 'TEXT',
        date: '4 days ago',
        ext: 'txt',
        category: 'Transcript',
        preview:
          'https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '6',
        name: 'Crime Scene Photo 01.png',
        size: '4.2 MB',
        lines: '1920x1080',
        type: 'IMAGE',
        date: '5 hours ago',
        ext: 'png',
        category: 'Evidence',
        preview:
          'https://images.unsplash.com/photo-1577495508048-b635879837f1?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '7',
        name: '911 Dispatch Recording.mp3',
        size: '8.5 MB',
        lines: '3:45',
        type: 'AUDIO',
        date: '1 day ago',
        ext: 'mp3',
        category: 'Audio',
        preview:
          'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '8',
        name: 'Body Cam Footage - Arrest.mp4',
        size: '124 MB',
        lines: '15:20',
        type: 'VIDEO',
        date: '2 hours ago',
        ext: 'mp4',
        category: 'Body Cam',
        preview:
          'https://images.unsplash.com/photo-1557597774-9d273605dfa9?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '9',
        name: 'Legal Research - 4th Amendment.md',
        size: '85 KB',
        lines: '450 lines',
        type: 'MARKDOWN',
        date: '2 days ago',
        ext: 'md',
        category: 'Research',
        preview:
          'https://images.unsplash.com/photo-1453928582365-b6ad33cbcf64?auto=format&fit=crop&q=80&w=400',
      },
      {
        id: '10',
        name: 'Drone Footage - Scene Overview.mp4',
        size: '850 MB',
        lines: '12:30',
        type: 'VIDEO',
        date: '1 day ago',
        ext: 'mp4',
        category: 'Drone Footage',
        preview:
          'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80&w=400',
      },
    ],
  },

  chat: {
    showcaseMessages: [
      {
        role: 'assistant' as const,
        content:
          'I have prepared a showcase of my different processing states below.',
      },
      {
        role: 'assistant' as const,
        content: '',
        steps: [
          {
            type: 'thinking' as const,
            label: 'Reviewing case facts and discovery materials...',
          },
        ],
      },
      {
        role: 'assistant' as const,
        content: '',
        steps: [
          { type: 'complete' as const, label: 'Reviewed case file' },
          {
            type: 'tool' as const,
            label: 'Executing: search discovery for "traffic stop"',
          },
        ],
      },
      {
        role: 'assistant' as const,
        content:
          'I have reviewed the provided discovery materials and identified several relevant references. The following sources were used to generate this response and are available for your review in the discovery manager.',
        steps: [
          { type: 'complete' as const, label: 'Reviewed 12 documents' },
          { type: 'complete' as const, label: 'Cross-referenced citations' },
        ],
        sources: [
          { id: 's1', name: 'Primary Document.pdf', type: 'PDF' },
          { id: 's2', name: 'Reference Material.mp4', type: 'VIDEO' },
          { id: 's3', name: 'Supporting Text.txt', type: 'TEXT' },
        ],
      },
    ],
    agentic: {
      initialStep: 'Initializing full discovery review...',
      seq1: [
        { type: 'complete' as const, label: 'Indexed 1,242 discovery files' },
        {
          type: 'tool' as const,
          label: 'Searching 842 pages of recorded statements...',
        },
      ],
      seq2: [
        { type: 'complete' as const, label: 'Indexed 1,242 discovery files' },
        {
          type: 'complete' as const,
          label: 'Reviewed all recorded statements',
        },
        {
          type: 'tool' as const,
          label:
            'Cross-referencing body cam timestamps with incident report narrative...',
        },
        {
          type: 'thinking' as const,
          label: 'Analyzing consistency across accounts...',
        },
      ],
      seq3Steps: [
        { type: 'complete' as const, label: 'Indexed 1,242 discovery files' },
        {
          type: 'complete' as const,
          label: 'Reviewed all recorded statements',
        },
        {
          type: 'complete' as const,
          label: 'Reviewed 15:20 of body cam footage',
        },
        {
          type: 'complete' as const,
          label: 'Identified 3 material inconsistencies',
        },
      ],
      finalContent:
        "After a comprehensive review of the 1,242 discovery files, I identified a material inconsistency worth examining.\n\nThe written incident report (Doc #4421) describes the defendant reaching toward the center console. The body cam footage from Officer Miller, synchronized to the same moment, shows the defendant's hands above the seat and in view throughout the stop.\n\nThis inconsistency may support a motion to suppress. I recommend reviewing the footage at 14:02–14:18 and comparing it against the report narrative before filing.",
      finalSources: [
        { id: 's1', name: 'Police Report - Incident 4421.pdf', type: 'PDF' },
        {
          id: 's2',
          name: 'Body Cam - Miller (14:02-15:10).mp4',
          type: 'VIDEO',
        },
        { id: 's3', name: 'Officer Miller Statement.txt', type: 'TEXT' },
      ],
    },
  },

  review: {
    dispositions: {
      unreviewed: 'Unreviewed',
      reviewed: 'Reviewed',
      corrected: 'Corrected',
      dismissed: 'Dismissed',
      followUp: 'Follow-up',
    },
    transcription: {
      na: 'N/A',
      queued: 'Queued',
      transcribed: 'Transcribed',
      flagged: 'Flagged',
      failed: 'Failed',
    },
    edge: {
      encrypted: 'Encrypted',
      corrupt: 'Corrupt',
      unsupported: 'Unsupported codec',
      zeroByte: 'Zero-byte',
    },
    sensitivity: {
      privileged: 'Privileged',
      sealed: 'Sealed',
      juvenile: 'Juvenile',
    },
    confidenceThreshold: 40,
    provenanceLabel: 'View source',
  },

  workflow: {
    title: 'Discovery Workflow',
    steps: [
      {
        id: 'ingest',
        label: 'Ingest',
        description: 'Scan folders, gate transcription spend',
        route: '/case/ingest',
        status: 'complete',
      },
      {
        id: 'inventory',
        label: 'Inventory',
        description: 'Review the master file table',
        route: '/case/files',
        status: 'active',
      },
      {
        id: 'workbench',
        label: 'Review',
        description: 'Verify transcripts & confidence',
        route: '/case/files/workbench',
        status: 'active',
      },
      {
        id: 'completeness',
        label: 'Completeness',
        description: 'Find gaps, draft supplemental requests',
        route: '/case/completeness',
        status: 'pending',
      },
      {
        id: 'sentencing',
        label: 'Sentencing',
        description: 'Surface considerations (gated)',
        route: '/case/sentencing',
        status: 'locked',
      },
    ],
    auditLogTitle: 'Audit Log',
    exportLabel: 'Export Package',
    statusLabels: {
      complete: 'Complete',
      active: 'In progress',
      pending: 'Pending',
      locked: 'Locked',
    },
    auditLog: {
      subtitle: 'Append-only record of every review action on this case.',
      entries: [
        {
          id: 'a1',
          actor: 'A. Reyes',
          action: 'Marked transcript verified',
          target: 'BWC_Davis_0412.mp4',
          time: '17 minutes ago',
        },
        {
          id: 'a2',
          actor: 'A. Reyes',
          action: 'Corrected segment 00:18 speaker label',
          target: 'BWC_Miller_0412.mp4',
          time: '24 minutes ago',
        },
        {
          id: 'a3',
          actor: 'System',
          action: 'Flagged low-confidence transcript (31)',
          target: 'BWC_Miller_0412.mp4',
          time: '1 hour ago',
        },
        {
          id: 'a4',
          actor: 'A. Reyes',
          action: 'Added gap to supplemental request',
          target: 'Bates range 000112–000139',
          time: '2 hours ago',
        },
        {
          id: 'a5',
          actor: 'System',
          action: 'Transcription completed (37 files)',
          target: 'Production_001 · Production_002',
          time: '3 hours ago',
        },
        {
          id: 'a6',
          actor: 'A. Reyes',
          action: 'Confirmed transcription selection',
          target: '37 spoken-audio files',
          time: '3 hours ago',
        },
        {
          id: 'a7',
          actor: 'System',
          action: 'Scan completed (82 files enumerated)',
          target: 'Production_001 · Production_002',
          time: '3 hours ago',
        },
      ],
    },
    exportPackage: {
      title: 'Export Package',
      subtitle:
        'Assemble a reviewed-set package. Exhibit stickers and Bates numbering are handled in Acrobat — Imperian exports a manifest only.',
      manifestNote: 'Export reviewed/selected set as manifest',
      items: [
        {
          id: 'e1',
          label: 'Inventory table',
          description: 'Full file inventory with confidence and review status',
          enabled: true,
        },
        {
          id: 'e2',
          label: 'Verified transcripts',
          description: 'Transcripts marked verified, with segment edits',
          enabled: true,
        },
        {
          id: 'e3',
          label: 'Supplemental request list',
          description: 'Assembled gaps as a draft request',
          enabled: true,
        },
        {
          id: 'e4',
          label: 'Sentencing memo draft',
          description: 'Accepted considerations grouped by section',
          enabled: false,
        },
        {
          id: 'e5',
          label: 'Reviewed-set manifest',
          description: 'Lightweight manifest of the selected set',
          enabled: true,
        },
      ],
      exportButton: 'Generate Package',
    },
  },

  ingest: {
    title: 'Ingest Discovery',
    subtitle:
      'Scan a folder to enumerate and describe files before transcription',
    headerTitle: 'Back to Case',
    pickerTitle: 'Source Folder',
    pickerHint: 'Select the production folder to scan',
    recursiveLabel: 'Include subfolders (recursive)',
    treePreviewTitle: 'Folder Preview',
    scanButton: 'Run Scan',
    scanningLabel: 'Scanning',
    cancelLabel: 'Cancel',
    cheapPassNote:
      'The scan performs enumeration, type detection, spoken-audio detection, and one-line descriptions only. No transcription runs at this stage.',
    tree: [
      {
        name: 'Production_001',
        type: 'folder',
        children: [
          { name: 'Police_Reports', type: 'folder', count: 14 },
          { name: 'Body_Cam', type: 'folder', count: 22 },
          { name: 'Audio_911', type: 'folder', count: 6 },
        ],
      },
      {
        name: 'Production_002',
        type: 'folder',
        children: [
          { name: 'Surveillance', type: 'folder', count: 31 },
          { name: 'Interviews', type: 'folder', count: 9 },
        ],
      },
    ],
    estimateTitle: 'Transcription Cost Estimate',
    estimateSubtitle:
      'Transcription only runs on the files you confirm below. This is the spend gate.',
    estimate: {
      spokenAudioCount: 37,
      totalDuration: '6h 42m',
      tokenRangeLow: '1.8M',
      tokenRangeHigh: '2.4M',
      costLow: '$72',
      costHigh: '$96',
    },
    selectAllLabel: 'Select all spoken-audio files',
    confirmButton: 'Transcribe Selected',
    estimateGroups: [
      {
        subfolder: 'Body_Cam',
        files: [
          { id: 'i1', name: 'BWC_Miller_0412.mp4', duration: '15:20' },
          { id: 'i2', name: 'BWC_Davis_0412.mp4', duration: '12:04' },
        ],
      },
      {
        subfolder: 'Audio_911',
        files: [
          { id: 'i3', name: 'Dispatch_2231.mp3', duration: '3:45' },
          { id: 'i4', name: 'Caller_2231.mp3', duration: '5:12' },
        ],
      },
    ],
  },

  inventory: {
    columns: {
      filename: 'Filename',
      type: 'Type',
      sizeDuration: 'Size / Duration',
      description: 'Description',
      transcription: 'Transcription',
      confidence: 'Confidence',
      reviewStatus: 'Review',
      actions: 'Actions',
    },
    spoken: { yes: 'Yes', no: 'No', uncertain: 'Uncertain' },
    actions: { open: 'Open', rerun: 'Re-run', flag: 'Flag' },
    bulk: {
      rerun: 'Re-run transcription',
      markReviewed: 'Mark reviewed',
      exportSelection: 'Export selection',
    },
    filters: {
      type: 'Type',
      transcription: 'Transcription',
      confidence: 'Confidence band',
      reviewStatus: 'Review status',
      all: 'All',
    },
    thresholdDividerLabel: 'Confidence threshold — 40',
    records: [
      {
        id: 'r1',
        name: 'BWC_Miller_0412.mp4',
        subfolder: 'Production_001 / Body_Cam',
        type: 'Video',
        ext: 'mp4',
        sizeDuration: '15:20',
        description:
          'Body-worn footage of the traffic stop and search of the vehicle.',
        spoken: 'Yes',
        transcription: 'Flagged',
        confidence: 31,
        reviewStatus: 'Follow-up',
        sensitivity: null,
      },
      {
        id: 'r2',
        name: 'Dispatch_2231.mp3',
        subfolder: 'Production_001 / Audio_911',
        type: 'Audio',
        ext: 'mp3',
        sizeDuration: '3:45',
        description: 'Initial 911 dispatch call reporting the incident.',
        spoken: 'Yes',
        transcription: 'Flagged',
        confidence: 38,
        reviewStatus: 'Unreviewed',
        sensitivity: null,
      },
      {
        id: 'r3',
        name: 'Caller_2231.mp3',
        subfolder: 'Production_001 / Audio_911',
        type: 'Audio',
        ext: 'mp3',
        sizeDuration: '5:12',
        description:
          'Caller statement; heavy background noise, overlapping speech.',
        spoken: 'Uncertain',
        transcription: 'Transcribed',
        confidence: 54,
        reviewStatus: 'Unreviewed',
        sensitivity: null,
      },
      {
        id: 'r4',
        name: 'Client_Interview_0520.txt',
        subfolder: 'Production_002 / Interviews',
        type: 'Doc',
        ext: 'txt',
        sizeDuration: '1,200 lines',
        description: 'Privileged client interview transcript from 5/20.',
        spoken: 'No',
        transcription: 'N/A',
        confidence: null,
        reviewStatus: 'Reviewed',
        sensitivity: 'privileged',
      },
      {
        id: 'r5',
        name: 'BWC_Davis_0412.mp4',
        subfolder: 'Production_001 / Body_Cam',
        type: 'Video',
        ext: 'mp4',
        sizeDuration: '12:04',
        description: 'Secondary officer body-cam; clear audio throughout.',
        spoken: 'Yes',
        transcription: 'Transcribed',
        confidence: 88,
        reviewStatus: 'Reviewed',
        sensitivity: null,
      },
      {
        id: 'r6',
        name: 'Police_Report_4421.pdf',
        subfolder: 'Production_001 / Police_Reports',
        type: 'Doc',
        ext: 'pdf',
        sizeDuration: '12 pages',
        description:
          'Primary incident report describing the arrest and search.',
        spoken: 'No',
        transcription: 'N/A',
        confidence: null,
        reviewStatus: 'Reviewed',
        sensitivity: null,
      },
      {
        id: 'r7',
        name: 'Surveillance_Lot_C.mp4',
        subfolder: 'Production_002 / Surveillance',
        type: 'Video',
        ext: 'mp4',
        sizeDuration: '48:00',
        description: 'Parking-lot surveillance; no speech detected.',
        spoken: 'No',
        transcription: 'N/A',
        confidence: null,
        reviewStatus: 'Unreviewed',
        sensitivity: null,
      },
      {
        id: 'r8',
        name: 'Sealed_Juvenile_Record.pdf',
        subfolder: 'Production_002 / Interviews',
        type: 'Doc',
        ext: 'pdf',
        sizeDuration: '4 pages',
        description: 'Sealed record — access restricted.',
        spoken: 'No',
        transcription: 'N/A',
        confidence: null,
        reviewStatus: 'Unreviewed',
        sensitivity: 'sealed',
      },
      {
        id: 'r9',
        name: 'Corrupt_Segment_07.mp4',
        subfolder: 'Production_001 / Body_Cam',
        type: 'Video',
        ext: 'mp4',
        sizeDuration: '—',
        description: 'File could not be read.',
        spoken: 'Uncertain',
        transcription: 'Failed',
        edge: 'corrupt',
        confidence: null,
        reviewStatus: 'Follow-up',
        sensitivity: null,
      },
      {
        id: 'r10',
        name: 'Encrypted_Drive_Image.dd',
        subfolder: 'Production_002 / Surveillance',
        type: 'Other',
        ext: 'dd',
        sizeDuration: '4.2 GB',
        description: 'Encrypted container; key not provided.',
        spoken: 'No',
        transcription: 'Failed',
        edge: 'encrypted',
        confidence: null,
        reviewStatus: 'Follow-up',
        sensitivity: null,
      },
    ],
  },

  workbench: {
    headerTitle: 'Back to Inventory',
    fileName: 'BWC_Miller_0412.mp4',
    subfolder: 'Production_001 / Body_Cam',
    mediaTitle: 'Media',
    transcriptTitle: 'Transcript',
    matrixTitle: 'Confidence Matrix',
    matrixSubtitle: 'Justification for the flag-or-keep decision.',
    verifiedToggle: 'Transcript verified',
    addSpeakerLabel: 'Edit speaker labels',
    matrix: {
      composite: 31,
      dimensions: [
        { label: 'Audio quality', value: 42 },
        { label: 'Background noise', value: 28 },
        { label: 'Speech legibility', value: 35 },
        { label: 'Speaker overlap', value: 22 },
      ],
    },
    segments: [
      {
        id: 'seg1',
        start: '00:04',
        speaker: 'Officer Miller',
        text: 'Step out of the vehicle and keep your hands where I can see them.',
        confidence: 84,
      },
      {
        id: 'seg2',
        start: '00:11',
        speaker: 'Defendant',
        text: '[inaudible] my hands are right here, I—',
        confidence: 29,
      },
      {
        id: 'seg3',
        start: '00:18',
        speaker: 'Officer Miller',
        text: 'Stop reaching toward the console.',
        confidence: 33,
      },
      {
        id: 'seg4',
        start: '00:24',
        speaker: 'Passenger',
        text: "He's not reaching for anything, his hands are up.",
        confidence: 71,
      },
    ],
    segmentActions: {
      accept: 'Accept',
      correct: 'Correct',
      dispute: 'Mark disputed',
    },
  },

  completeness: {
    headerTitle: 'Back to Case',
    title: 'Completeness Review',
    subtitle:
      'Gaps detected across the production. Assemble a supplemental request.',
    referentialTitle: 'Referential Gaps',
    sequenceTitle: 'Sequence Gaps',
    composerTitle: 'Supplemental Discovery Request',
    composerSubtitle:
      'Selected gaps assemble into an editable, exportable request.',
    exportLetter: 'Export as Letter',
    exportMotion: 'Export as Motion',
    addToRequest: 'Add to request',
    gapType: { referential: 'Referential', sequence: 'Sequence' },
    referentialGaps: [
      {
        id: 'g1',
        description:
          "Incident report cites 'Officer bodycam, Exhibit B' — no corresponding file exists in the production.",
        source: 'Police_Report_4421.pdf, p.3',
        type: 'referential',
        severity: 88,
      },
      {
        id: 'g2',
        description:
          "Plea offer references a 'lab analysis report' that was not produced.",
        source: 'Prosecution_Plea_Offer.pdf, p.2',
        type: 'referential',
        severity: 64,
      },
    ],
    sequenceGaps: [
      {
        id: 'g3',
        description:
          'Bates range BATES-000112 through BATES-000139 is missing.',
        source: 'Production_001 index',
        type: 'sequence',
        severity: 72,
      },
      {
        id: 'g4',
        description:
          'Evidence item #14 referenced in log; no item #14 produced.',
        source: 'Evidence_Log.pdf',
        type: 'sequence',
        severity: 51,
      },
    ],
  },

  sentencing: {
    headerTitle: 'Back to Case',
    title: 'Sentencing Considerations',
    subtitle: 'AI-surfaced considerations organized by guideline section.',
    lockedTitle: 'Locked until discovery is complete',
    lockedBody:
      'Mark the completeness review as complete to surface sentencing considerations.',
    unlockButton: 'Mark discovery complete (demo)',
    banner:
      'These are AI-surfaced considerations for attorney judgment — not legal conclusions.',
    dispositions: { accept: 'Accept', note: 'Note', dismiss: 'Dismiss' },
    annotationPlaceholder: 'Add an attorney note...',
    sections: [
      {
        id: 's2',
        label: '§2 — Offense Conduct',
        items: [
          {
            id: 'c1',
            observation:
              'Body-cam footage may contradict the report narrative regarding the reach toward the console, which could bear on offense-level enhancements.',
            source: 'BWC_Miller_0412.mp4 @ 00:18',
            confidence: 62,
          },
        ],
      },
      {
        id: 's3',
        label: '§3 — Adjustments',
        items: [
          {
            id: 'c2',
            observation:
              'Passenger statement suggests cooperation at the scene; potentially relevant to acceptance-of-responsibility analysis.',
            source: 'BWC_Miller_0412.mp4 @ 00:24',
            confidence: 48,
          },
        ],
      },
      {
        id: 's4',
        label: '§4 — Criminal History',
        items: [
          {
            id: 'c3',
            observation:
              'No prior felony convictions noted in the case summary.',
            source: 'Case_Summary',
            confidence: 91,
          },
        ],
      },
      {
        id: 's5',
        label: '§5 — Departures / Variances',
        items: [
          {
            id: 'c4',
            observation:
              'Contested search circumstances may support a variance argument if the motion to suppress is partially successful.',
            source: 'Motion_to_Suppress_Draft.docx',
            confidence: 40,
          },
        ],
      },
    ],
  },

  redaction: {
    headerTitle: 'Back to Inventory',
    title: 'Document Redaction',
    subtitle:
      'Automatically redact sensitive information and generate sanitized documents for disclosure.',
    fileName: 'Client_Interview_0520.txt',
    addRuleButton: 'Add Redaction Rule',
    previewButton: 'Preview Changes',
    generateButton: 'Generate Sanitized Document',
    cancelButton: 'Cancel',
    rulesTitle: 'Redaction Rules',
    rulesSubtitle:
      'Define patterns to find and redact. The system will detect all format variations automatically.',
    previewTitle: 'Preview',
    previewSubtitle:
      'Review redacted content before generating the final document.',
    matchesLabel: 'matches found',
    ruleTypes: {
      name: 'Name',
      date: 'Date',
      ssn: 'Social Security Number',
      address: 'Address',
      phone: 'Phone Number',
      custom: 'Custom Pattern',
    },
    placeholders: {
      findLabel: 'Find',
      findPlaceholder: 'e.g., Thompson',
      replaceLabel: 'Replace with',
      replacePlaceholder: 'e.g., Defendant',
      typeLabel: 'Type',
    },
    exampleRules: [
      {
        id: 'r1',
        type: 'name',
        find: 'Thompson',
        replace: 'Defendant',
        matches: 47,
      },
      {
        id: 'r2',
        type: 'date',
        find: 'January 15, 1985',
        replace: '[DATE OF BIRTH REDACTED]',
        matches: 12,
      },
      {
        id: 'r3',
        type: 'ssn',
        find: '123-45-6789',
        replace: '[SSN REDACTED]',
        matches: 8,
      },
    ],
    previewContent: `INTERVIEW TRANSCRIPT
Date: May 20, 2024
Subject: [DEFENDANT] (DOB: [DATE OF BIRTH REDACTED])
SSN: [SSN REDACTED]

Q: Please state your full name for the record.
A: My name is [DEFENDANT].

Q: And your date of birth?
A: [DATE OF BIRTH REDACTED].

Q: Can you describe what happened on the evening of April 12th?
A: I was driving home from work around 9 PM when I was pulled over...

[Additional content redacted for preview]`,
  },
} as const
